import Button from "@mui/material/Button";
import { Form } from "@rjsf/mui";
import { RJSFSchema, RJSFValidationError, UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchSkpEnvironment, resolveVanityLink } from "../../api";
import Avatar from "../../components/avatar";
import { Spacer } from "../../components/layout";
import {
  ApiException,
  FieldsPrefillData,
  FormPrefillType,
  SkpEnvironment,
  MailboxDetails,
  TransferDraft,
  TransferDraftErrors,
} from "../../entities";
import { featureFlags } from "../../utils/featureFlags";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import {
  EnvironmentProvider,
  useSkpEnvironment,
} from "../../providers/environment";
import {
  TransferDraftAndOuterDropzoneProvider,
  useOuterDropzoneState,
  useTransferDraft,
  useTransferDraftDispatch,
} from "../../providers/transferDraft";
import { TransferDraftActionType } from "../../reducers/transferDraft";
import { isApiException, getErrorMessage } from "../../utils/typeGuard";
import { getHumanReadableFileSize } from "../../utils/units";
import { ApiExceptionErrorPage, ErrorPage } from "../error";
import NotFoundPage from "../NotFound";
import MailboxPageSkeleton from "../skeletons/Mailbox";
import {
  AddedFilesChips,
  DropAndFileChipsZone,
} from "./components/dropAndFileChipsZone";
import SendButton from "./components/send";
import EmailTextField from "./components/textFields/emailTextField";
import MessageTextField from "./components/textFields/message";
import NameTextField from "./components/textFields/nameTextField";
import SubjectTextField from "./components/textFields/subjectTextField";
import MailboxLoadingScreen from "./mailboxLoadingScreen";
import FormThemeProvider from "../../providers/muiThemeProvider/formThemeProvider";
import {
  FieldTemplate,
  TitleFieldTemplate,
  DescriptionFieldTemplate,
  ObjectFieldTemplate,
  CheckboxWidget,
  RadioWidget,
  CheckboxesWidget,
  NumberField,
} from "../../templates/formTemplates";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Lock from "@mui/icons-material/Lock";
import { Popover, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AbsenceModeOverlay,
  VolumeLimitExceededOverlay,
} from "../absenceModeOverlay";
import { normalizeUiSchema } from "../../utils/structuredFields";
import { decodeBase64UrlJson } from "../../utils/url";
import { constants } from "../../utils/constants";
import { i18n } from "i18next";
import {
  extractDynamicPrefillFromMailboxPayload,
  extractImmutableDynamicKeysFromMailboxPayload,
} from "../../utils/prefill";

type WidgetUiSchema = UiSchema & {
  "ui:widget"?: string;
  "ui:options"?: {
    widget?: string;
  };
};

type UsernameProp = {
  username: string;
};

type AddFilesButtonProp = {
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
};

export default function Mailbox() {
  const { t } = useTranslation();
  const { vanityLink } = useParams();

  const {
    status: detailsEnvStatus,
    data: detailsEnvData,
    error: detailsEnvError,
  } = useQuery({
    queryKey: ["mailboxDetailsAndEnvironment", vanityLink],
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      return Promise.all([
        resolveVanityLink(vanityLink!),
        fetchSkpEnvironment(),
      ]);
    },
  });

  switch (detailsEnvStatus) {
    case "loading":
      return (
        <MailboxPageSkeleton
          childrenVerticalAlignment="start"
          hasMailboxLoaded={false}
        >
          <MailboxLoadingScreen />
        </MailboxPageSkeleton>
      );

    case "error":
      const error = detailsEnvError as unknown as Error | AxiosError;
      if (error instanceof AxiosError && error.response) {
        if (error.response?.status === 404) {
          return <NotFoundPage />;
        }

        const isErrorApiException: boolean = isApiException(
          error.response.data
        );

        if (isErrorApiException) {
          const apiException: ApiException = error.response
            .data as ApiException;

          const errorMessage: string = getErrorMessage(apiException, t);

          return <ApiExceptionErrorPage errorMessage={errorMessage} />;
        }
      }

      return <ErrorPage errorMessage={(error as Error).message} />;

    case "success":
      const [mailboxDetails, environment] = detailsEnvData!;

      return (
        <EnvironmentProvider environment={environment}>
          <TransferDraftAndOuterDropzoneProvider>
            <div className="relative animate-fade-in select-none">
              <MailboxPageSkeleton
                childrenVerticalAlignment="start"
                hasMailboxLoaded={true}
              >
                <Content mailboxDetails={mailboxDetails} />
              </MailboxPageSkeleton>
            </div>
          </TransferDraftAndOuterDropzoneProvider>
        </EnvironmentProvider>
      );
  }
}

const Username = ({ username }: UsernameProp) => {
  return (
    <div className="bsm:py-7 truncate text-[16px] font-medium sm:text-2xl">
      {username}
    </div>
  );
};

const AddFilesButton = ({ onClick }: AddFilesButtonProp) => {
  const { t } = useTranslation();
  return (
    <Button variant="outlined" onClick={onClick}>
      {t(`Add file(s)`)}
    </Button>
  );
};

const AddedFilesCountAndSize = () => {
  const { t } = useTranslation();
  const transferDraft: TransferDraft = useTransferDraft();
  const cumulativeAddedFilesSize: number = transferDraft.files.reduce(
    (accumulator, currentFile) => accumulator + currentFile[0].size,
    0
  );
  const addedFilesCount: number = transferDraft.files.length;

  return (
    <div className="text-[11px] text-[#747474]">
      {addedFilesCount}
      {addedFilesCount > 1 ? t(` files`) : t(` file`)} (
      {getHumanReadableFileSize(cumulativeAddedFilesSize)})
    </div>
  );
};

type SloganProp = {
  slogan: string | null;
};

/**
 * Generic or custom slogan set by the mailbox owner.
 * If mailbox owner has not set a slogan then a generic
 * slogan is return wrapped in a fragment.
 *
 * @param slogan Custom slogan set by the mailbox owner.
 * It could be null, if the mailbox owner has not set any slogan.
 *
 * @returns
 */
const Slogan = ({ slogan }: SloganProp) => {
  const { t } = useTranslation();
  return <>{slogan || t("Send me a message to my secure mailbox!")}</>;
};

type ContentProp = {
  mailboxDetails: MailboxDetails;
};

type MimeFilterInfoRowProps = {
  description: string;
  label: string;
  isMobileWidth: boolean;
};

const MimeFilterInfoRow = ({
  description,
  label,
  isMobileWidth,
}: MimeFilterInfoRowProps) => {
  // MUI Tooltip is unreliable on touch
  // so, we use Popover for mobile tapto-open mimetype description
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!isMobileWidth) {
      setPopoverAnchor(null);
    }
  }, [isMobileWidth]);

  const iconAndLabel = (
    <>
      <FontAwesomeIcon
        icon={faCircleInfo as IconProp}
        className="h-[12px] w-[12px] shrink-0"
        aria-hidden
      />
      <span>{label}</span>
    </>
  );

  const buttonClassName = `inline-flex w-fit max-w-full flex-row items-center gap-2 text-[12px] select-none border-0 bg-transparent p-0 font-inherit ${
    isMobileWidth ? "cursor-pointer" : "cursor-default"
  }`;

  if (isMobileWidth) {
    return (
      <span className="inline-flex w-fit max-w-full self-start">
        <button
          type="button"
          className={buttonClassName}
          style={{ color: "var(--primary-color)" }}
          tabIndex={0}
          aria-expanded={Boolean(popoverAnchor)}
          aria-haspopup="true"
          onClick={(e) => {
            e.stopPropagation();
            const el = e.currentTarget;
            setPopoverAnchor((prev) => (prev === el ? null : el));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              const el = e.currentTarget;
              setPopoverAnchor((prev) => (prev === el ? null : el));
            }
          }}
        >
          {iconAndLabel}
        </button>
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          marginThreshold={8}
          elevation={0}
          BackdropProps={{ invisible: true }}
          PaperProps={{
            sx: (theme) => ({
              maxWidth: 360,
              bgcolor: alpha(theme.palette.grey[700], 0.92),
              color: theme.palette.common.white,
              // `sx` multiplies numeric borderRadius by theme.shape.borderRadius.
              // `1` matches MuiTooltip (same as raw shape value).
              borderRadius: 1,
              px: 1,
              py: 0.5,
              whiteSpace: "pre-wrap",
              fontSize: theme.typography.pxToRem(11),
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeightMedium,
              wordWrap: "break-word",
              boxShadow: "none",
            }),
          }}
        >
          {description}
        </Popover>
      </span>
    );
  }

  const title = (
    <span style={{ whiteSpace: "pre-wrap", display: "block" }}>
      {description}
    </span>
  );

  return (
    <span className="inline-flex w-fit max-w-full self-start">
      <Tooltip
        title={title}
        placement="bottom-start"
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, 8] },
            },
          ],
        }}
        componentsProps={{
          tooltip: {
            sx: { maxWidth: 360 },
          },
        }}
      >
        <button
          type="button"
          className={buttonClassName}
          style={{ color: "var(--primary-color)" }}
          tabIndex={-1}
        >
          {iconAndLabel}
        </button>
      </Tooltip>
    </span>
  );
};

const Content = ({ mailboxDetails }: ContentProp) => {
  const { i18n, t } = useTranslation();
  const outerDropzoneState = useOuterDropzoneState();
  const transferDraft = useTransferDraft();
  const transferDraftDispatch = useTransferDraftDispatch();
  const environment: SkpEnvironment = useSkpEnvironment()!;
  const { isMobileWidth } = useWindowDimensions();
  // to check if prefill have been applied for reply-transfer
  const didApplyPrefillRef = useRef<boolean>(false);
  // to check if mailbox-prefill (via query param) has been applied
  const didApplyMailboxPrefillRef = useRef<boolean>(false);

  // guard against re-initializing the draft when effects re-run (e.g. object identity changes)
  const lastInitializedKeyRef = useRef<string | null>(null);
  // guard against re-applying the same mailbox-prefill payload on schema ref churn
  const lastMailboxPrefillEncodedRef = useRef<string | null>(null);

  // immutable static fields coming from mailbox-prefill payload
  const [immutableStaticFields, setImmutableStaticFields] = useState<
    Set<"name" | "email" | "subject" | "description">
  >(new Set());

  // dynamic fields that should be rendered as non-editable (from mailbox-prefill payload)
  const [immutableDynamicFields, setImmutableDynamicFields] = useState<
    Set<string>
  >(new Set());

  const isImmutableStatic = (
    key: "name" | "email" | "subject" | "description"
  ) => immutableStaticFields.has(key);

  const pageContainerRef = useRef<HTMLDivElement | null>(null);
  // TODO: Remove this ref when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
  const attachmentsLabelRef = useRef<HTMLParagraphElement | null>(null);

  const areFilesAdded: boolean = transferDraft.files.length > 0;

  // TODO: Remove these two variables when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
  const isFileCountInvalid =
    !featureFlags.noFilesTransfer &&
    transferDraft.errors.has(TransferDraftErrors.invalidFileCount);
  const attachFileTextColor = isFileCountInvalid
    ? `var(--warning-color)`
    : "black";

  const isAbsent: boolean = mailboxDetails.absent;
  const isVolumeLimitExceeded: boolean = mailboxDetails.volumeLimitExceeded;

  const slogan: string | null = mailboxDetails.slogan;
  const isOverlayActive: boolean = isAbsent || isVolumeLimitExceeded;

  const mimeFilterDescriptionRaw: string =
    mailboxDetails.mimeTypeFilterDescription ?? "";
  const showMimeFilterInfoRow: boolean =
    mailboxDetails.mimeTypeFilterEnabled &&
    mimeFilterDescriptionRaw.trim().length > 0;

  const formRef = useRef<any>(null);
  const pendingValidationResolveRef = useRef<null | ((ok: boolean) => void)>(
    null
  );

  // `i18n.language` can change for many reasons (auto language detection, fallback,
  // resource loading, programmatic calls). We only want to revalidate the dynamic RJSF
  // form when the USER explicitly switched the locale in the UI.
  const didMountRef = useRef(false);
  const manualLocaleChangeRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      manualLocaleChangeRef.current = true;
    };

    window.addEventListener("skp:manual-locale-change", handler);
    return () =>
      window.removeEventListener("skp:manual-locale-change", handler);
  }, []);

  const dynamicSchema = mailboxDetails.fieldsSchema as RJSFSchema | undefined;
  const dynamicUiSchema = normalizeUiSchema(
    mailboxDetails.uiSchema ?? {},
    dynamicSchema
  ) as UiSchema;

  const hasDynamicForm =
    !!dynamicSchema &&
    dynamicSchema.type === "object" &&
    dynamicSchema.properties &&
    Object.keys(dynamicSchema.properties).length > 0;

  const dynamicUiSchemaWithImmutable = useMemo(() => {
    if (!hasDynamicForm) return dynamicUiSchema;

    // shallow clone is not enough because we will edit nested keys.
    // uiSchema is plain JSON (no functions), so JSON cloning is fine here.
    const next = JSON.parse(JSON.stringify(dynamicUiSchema ?? {})) as UiSchema;

    immutableDynamicFields.forEach((fieldKey) => {
      const current = (next as any)[fieldKey];
      (next as any)[fieldKey] = {
        ...(current && typeof current === "object" ? current : {}),
        // RJSF will pass these into widget props as `disabled` / `readonly`.
        "ui:disabled": true,
        "ui:readonly": true,
      };
    });

    return next;
  }, [dynamicUiSchema, immutableDynamicFields, hasDynamicForm]);

  const scrollToFirstErrorOnPage = () => {
    const container = pageContainerRef.current;
    if (!container) return;

    const first =
      container.querySelector<HTMLElement>(
        'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"], [role="combobox"][aria-invalid="true"]'
      ) ||
      container.querySelector<HTMLElement>(
        ".MuiFormControl-root.Mui-error input, .MuiFormControl-root.Mui-error textarea, .MuiFormControl-root.Mui-error [role='combobox'], .MuiFormControl-root.Mui-error .MuiSelect-select"
      ) ||
      container.querySelector<HTMLElement>(
        '.MuiFormHelperText-root.Mui-error, [role="alert"], .MuiAlert-root'
      );

    if (first) {
      first.scrollIntoView({ behavior: "smooth", block: "center" });
      (first as any).focus?.({ preventScroll: true }); // nice UX
      return;
    }

    // TODO: Remove this block when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
    if (!featureFlags.noFilesTransfer) {
      if (transferDraft.errors.has(TransferDraftErrors.invalidFileCount)) {
        attachmentsLabelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  // scroll after all error states are rendered
  const scheduleScrollToFirstErrorOnPage = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToFirstErrorOnPage();
      });
    });
  };

  // RJSF keeps `errors` in internal state and does NOT recompute them
  // when only the app language changes. Force a re-validation so
  // `transformErrors` runs again and produces localized messages.
  //
  //! We run this only on manual locale changes (via LocaleSwitcher),
  //! not on initial language detection or other programmatic changes.
  useEffect(() => {
    if (!hasDynamicForm) return;

    // Skip the first effect run (initial render). Even with this guard, `i18n.language`
    // may still change later due to auto-detection, so we ALSO require the manual flag.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    // Only run when the LocaleSwitcher dispatched the manual-change event.
    // This keeps validation from running on init/fallback/programmatic language changes.
    if (!manualLocaleChangeRef.current) return;
    manualLocaleChangeRef.current = false;

    const api = formRef.current;
    if (!api?.validateForm) return;

    // Only re-validate if the form is already showing errors.
    // Otherwise, a locale switch would surface validation errors even though
    // the user hasn't clicked send btn yet.
    const hasExistingErrors =
      (Array.isArray((api as any)?.state?.errors) &&
        (api as any).state.errors.length > 0) ||
      ((api as any)?.state?.errorSchema &&
        typeof (api as any).state.errorSchema === "object" &&
        Object.keys((api as any).state.errorSchema).length > 0);

    if (!hasExistingErrors) return;

    // `validateForm()` recomputes AJV errors and updates RJSF internal error state.
    // This makes `transformErrors(...)` run again so messages are regenerated in the
    // currently selected locale. It does NOT execute submit handlers.
    requestAnimationFrame(() => {
      api.validateForm();
    });
  }, [hasDynamicForm, i18n.language]);

  // Init + reply-to-sender prefill
  // this intentionally does not depend on `dynamicSchema` so there's no risk of re-initializing mid-session
  // when the structured schema reference changes (e.g. locale switch / normalization)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const replyEncoded = hashParams.get(constants.replyHashParam);

    const initialize = () => {
      const initKey = `${mailboxDetails.uid}::${environment.server_version}`;
      if (lastInitializedKeyRef.current === initKey) return;

      transferDraftDispatch({
        type: TransferDraftActionType.initialize,
        mailboxDetails: mailboxDetails,
        environment: environment,
      });

      lastInitializedKeyRef.current = initKey;
    };

    // Avoid wiping user edits if the effect re-runs for any reason.
    if (replyEncoded && didApplyPrefillRef.current) return;

    // Reply-to-sender prefill
    if (replyEncoded) {
      const applyReplyPrefill = (
        data: FieldsPrefillData | Partial<FieldsPrefillData>
      ) => {
        // Intent is determined by the presence of the reply-hash param.
        // Therefore the payload does not need to contain `type`.
        if (data.mailboxUid && data.mailboxUid !== mailboxDetails.uid) {
          return false;
        }

        const withType: FieldsPrefillData = {
          type: FormPrefillType.replyPrefill,
          mailboxUid: data.mailboxUid,
          name: data.name,
          email: data.email,
        };

        transferDraftDispatch({
          type: TransferDraftActionType.prefillFields,
          fieldsPrefillData: withType,
          mailboxDetails: mailboxDetails,
          environment: environment,
        });

        return true;
      };

      try {
        const data = decodeBase64UrlJson<
          FieldsPrefillData | Partial<FieldsPrefillData>
        >(replyEncoded);

        const applied = applyReplyPrefill(data);
        if (applied) {
          didApplyPrefillRef.current = true;
        } else {
          initialize();
        }
      } catch {
        initialize();
      }

      return;
    }

    // Normal mailbox load (initialize once per mailbox/environment)
    initialize();
  }, [environment, mailboxDetails, transferDraftDispatch]);

  // Mailbox prefill via query param (`pre`) + dynamic structured fields.
  // This effect is allowed to depend on `dynamicSchema`, but must NEVER re-initialize the draft.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // URLSearchParams uses form-urlencoded semantics where `+` becomes a space.
    // If callers put standard base64 in the query string, normalize spaces back to `+`.
    const mailboxEncodedRaw = searchParams.get("pre");
    const mailboxEncoded = mailboxEncodedRaw
      ? mailboxEncodedRaw.replace(/\s/g, "+")
      : null;

    if (!mailboxEncoded) return;

    // Avoid re-applying the exact same payload when `dynamicSchema` ref churns.
    if (
      didApplyMailboxPrefillRef.current &&
      lastMailboxPrefillEncodedRef.current === mailboxEncoded
    ) {
      return;
    }

    const allowedImmutable = new Set([
      "name",
      "email",
      "subject",
      "description",
    ] as const);

    try {
      const payload =
        decodeBase64UrlJson<Record<string, unknown>>(mailboxEncoded);

      const data: FieldsPrefillData = {
        type: FormPrefillType.mailboxPrefill,
        mailboxUid: (payload as any).mailboxUid,
        name: (payload as any).name,
        email: (payload as any).email,
        subject: (payload as any).subject,
        description: (payload as any).description,
        immutable: (payload as any).immutable,
      };

      if (data.mailboxUid && data.mailboxUid !== mailboxDetails.uid) return;

      // immutable static fields
      type ImmutableStaticKey = "name" | "email" | "subject" | "description";

      const immutable = new Set<ImmutableStaticKey>(
        Array.isArray(data.immutable)
          ? data.immutable.filter(
              (k): k is ImmutableStaticKey =>
                typeof k === "string" && allowedImmutable.has(k as any)
            )
          : []
      );
      setImmutableStaticFields(immutable);

      // dynamic fields can only be extracted when the schema is present.
      const dynamicPrefill = dynamicSchema
        ? extractDynamicPrefillFromMailboxPayload(payload, dynamicSchema)
        : null;

      const prefilledStaticKeys = (
        ["name", "email", "subject", "description"] as const
      ).filter((k) => Object.prototype.hasOwnProperty.call(payload, k));

      const prefilledDynamicKeys = Object.keys(dynamicPrefill ?? {});

      const immutableDynamicKeys = dynamicSchema
        ? extractImmutableDynamicKeysFromMailboxPayload(
            (payload as any).immutable,
            dynamicSchema
          )
        : new Set<string>();

      setImmutableDynamicFields(immutableDynamicKeys);

      transferDraftDispatch({
        type: TransferDraftActionType.prefillFields,
        fieldsPrefillData: data,
        mailboxDetails,
        environment,
        dynamicFormData: dynamicPrefill,
        prefillKeys: {
          static: [...prefilledStaticKeys],
          dynamic: prefilledDynamicKeys,
        },
      });

      didApplyMailboxPrefillRef.current = true;
      lastMailboxPrefillEncodedRef.current = mailboxEncoded;
    } catch {
      // ignore invalid payload
    }
  }, [environment, mailboxDetails, transferDraftDispatch, dynamicSchema]);

  const handleValidateDynamicForm = (): Promise<boolean> => {
    if (!hasDynamicForm || !formRef.current) {
      // nothing to validate
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      pendingValidationResolveRef.current = resolve;
      // Triggers RJSF validation
      //  onSubmit/onError will decide the result
      formRef.current.submit();
    });
  };

  // This makes sure that shape of field values obeys `type`
  // which we receive in fieldSchema
  const sanitizeStructuredFormData = (
    schema: RJSFSchema | undefined,
    data: any
  ) => {
    if (!schema || schema.type !== "object" || !schema.properties)
      return (data ?? {}) as any;

    const next: Record<string, any> = {
      ...(data && typeof data === "object" ? data : {}),
    };

    for (const key of Object.keys(schema.properties)) {
      const fieldSchema: any = (schema.properties as any)[key];
      const v = next[key];

      if (
        fieldSchema?.type === "array" &&
        Array.isArray(fieldSchema?.items?.enum)
      ) {
        const allowed: any[] = fieldSchema.items.enum;

        // Keep missing enum-arrays as `undefined` so AJV `required` errors still work.
        // But normalize RJSF checkbox-group boolean flags (e.g. [false, false]) to string[]
        // to avoid AJV type errors like "Newsletter.0 must be string".

        // NOTE: Many checkbox groups are configured with `minItems: 1` (instead of `required`).
        // AJV does not validate `minItems` when the property is missing/undefined.
        // Normalize missing values to [] ONLY when minItems > 0 so the field can show an error state.
        if (
          (v === undefined || v === null) &&
          typeof fieldSchema.minItems === "number" &&
          fieldSchema.minItems > 0
        ) {
          next[key] = [];
          continue;
        }

        // transform boolean flags to selected strings (usually [])
        if (
          Array.isArray(v) &&
          v.length === allowed.length &&
          v.length > 0 &&
          v.every((x: any) => typeof x === "boolean")
        ) {
          next[key] = allowed.filter((_: any, idx: number) => v[idx] === true);
          continue;
        }

        // transforms objects to primitive values
        if (Array.isArray(v)) {
          next[key] = v
            .map((item: any) => {
              if (item && typeof item === "object") {
                if ("value" in item) return item.value;
                if ("const" in item) return item.const;
              }
              return item;
            })
            .filter((x: any) => x != null);
        }

        continue;
      }
    }

    return next;
  };

  const sanitizedDynamicFormData = sanitizeStructuredFormData(
    dynamicSchema,
    transferDraft.dynamicFormData ?? {}
  );

  return (
    <>
      {isAbsent && (
        <AbsenceModeOverlay
          uid={mailboxDetails.uid}
          username={mailboxDetails.name}
          email={mailboxDetails.email}
          absenceMessage={mailboxDetails.absenceMessage}
        />
      )}
      {!isAbsent && isVolumeLimitExceeded && (
        <VolumeLimitExceededOverlay
          uid={mailboxDetails.uid}
          username={mailboxDetails.name}
          email={mailboxDetails.email}
        />
      )}
      <div
        className={`flex flex-col items-center ${
          isOverlayActive ? "pointer-events-none select-none" : ""
        }`}
        aria-hidden={isOverlayActive || undefined}
        onKeyDown={isOverlayActive ? (e) => e.preventDefault() : undefined}
      >
        <div
          ref={pageContainerRef}
          className="container max-w-screen-lg md:w-3/4"
        >
          <div className="mb-7 flex flex-row items-start sm:m-0 sm:items-center">
            <Avatar
              uid={mailboxDetails.uid}
              name={mailboxDetails.name ?? ""}
              size={55}
              isAnonymous={false}
            />
            <div className="w-5" />
            <div className="flex flex-col">
              <Username username={mailboxDetails.name ?? ""} />
              {mailboxDetails.secure && <SecureMailboxBadge />}
              <div className="mt-1 text-sm sm:hidden">
                <Slogan slogan={slogan} />
              </div>
            </div>
            <Spacer />
          </div>
          <div className="flex flex-col gap-y-5">
            <div className="hidden sm:block">
              <div className="flex flex-row items-center justify-between pt-3 text-xl">
                <Slogan slogan={slogan} />
              </div>
            </div>
            <div className="flex flex-col gap-y-5 sm:flex-row sm:gap-x-5">
              <NameTextField disabled={isImmutableStatic("name")} />
              <EmailTextField disabled={isImmutableStatic("email")} />
            </div>
            <SubjectTextField disabled={isImmutableStatic("subject")} />
            <MessageTextField disabled={isImmutableStatic("description")} />
            <div>
              {/* TODO: When lifting flag `REACT_APP_FF_NO_FILES_TRANSFER`: drop ref + style, keep className="text-base text-black" */}
              <p
                ref={attachmentsLabelRef}
                className="text-base"
                style={{ color: attachFileTextColor }}
              >
                {transferDraft.files!.length > 0
                  ? t(`Attached file(s):`)
                  : t(`Would you like to add an attachment?`)}
              </p>

              {showMimeFilterInfoRow && (
                <MimeFilterInfoRow
                  description={mimeFilterDescriptionRaw}
                  label={t("Information about allowed file formats")}
                  isMobileWidth={isMobileWidth}
                />
              )}
              {isMobileWidth ? (
                areFilesAdded ? (
                  <AddedFilesChips />
                ) : (
                  <></>
                )
              ) : (
                <DropAndFileChipsZone />
              )}
              {isMobileWidth || areFilesAdded ? (
                <div className="flex items-center justify-between">
                  <AddFilesButton onClick={outerDropzoneState.open} />
                  <AddedFilesCountAndSize />
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className="inline-flex"></div>
          <div>
            {hasDynamicForm && (
              <FormThemeProvider>
                <Form
                  ref={formRef}
                  schema={dynamicSchema!}
                  uiSchema={dynamicUiSchemaWithImmutable}
                  formData={sanitizedDynamicFormData}
                  validator={validator}
                  liveValidate={false}
                  noHtml5Validate
                  focusOnFirstError
                  showErrorList={false}
                  transformErrors={buildTransformErrors(
                    dynamicSchema,
                    dynamicUiSchema,
                    i18n
                  )}
                  onChange={(e) => {
                    const cleaned = sanitizeStructuredFormData(
                      dynamicSchema,
                      e.formData ?? {}
                    );
                    transferDraftDispatch({
                      type: TransferDraftActionType.dynamicFormUpdate,
                      data: cleaned as Record<string, unknown>,
                    });
                  }}
                  onSubmit={(e) => {
                    // Ensure we have the final formData when validation succeeded
                    transferDraftDispatch({
                      type: TransferDraftActionType.dynamicFormUpdate,
                      data: (e.formData ?? null) as Record<
                        string,
                        unknown
                      > | null,
                    });

                    pendingValidationResolveRef.current?.(true);
                    pendingValidationResolveRef.current = null;
                  }}
                  onError={(errors) => {
                    pendingValidationResolveRef.current?.(false);
                    pendingValidationResolveRef.current = null;

                    scheduleScrollToFirstErrorOnPage();
                  }}
                  templates={{
                    FieldTemplate,
                    TitleFieldTemplate,
                    DescriptionFieldTemplate,
                    ObjectFieldTemplate,
                  }}
                  widgets={{
                    radio: RadioWidget,
                    checkbox: CheckboxWidget,
                    checkboxes: CheckboxesWidget,
                    updown: NumberField,
                  }}
                />
              </FormThemeProvider>
            )}
          </div>
          <div className="flex flex-row items-center justify-between">
            <p>
              {transferDraft.ttl === 0
                ? t(
                    `Your message will be available for download by the recipient for one day`
                  )
                : t(
                    `Your message will be available for download by the recipient for {{validity}} days`,
                    { validity: `${transferDraft.ttl}` }
                  )}
            </p>
          </div>
          <div
            className={
              isMobileWidth
                ? "sticky bottom-0 z-10 bg-white/[0.7] pb-11 pt-6 backdrop-blur-[3px]"
                : "flex justify-end"
            }
          >
            <SendButton
              onValidationFailed={scheduleScrollToFirstErrorOnPage}
              validateDynamicForm={
                hasDynamicForm ? handleValidateDynamicForm : undefined
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

const SecureMailboxBadge = () => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t(
        `This mailbox uses enhanced security. Recipients must sign in to access the data.`
      )}
    >
      <div className="-mt-1 inline-flex cursor-default items-center justify-start text-[11px] text-[#B3B3B3]">
        <div className="relative inline-flex items-center overflow-hidden rounded-sm">
          <span
            className="
              pointer-events-none
              absolute inset-y-0 left-0
              w-8
              animate-shine-left-to-right bg-gradient-to-r from-transparent via-white/70
              to-transparent
            "
          />
          <Lock fontSize="inherit" className="mr-1" />
          {t(`Increased security level`)}
        </div>
      </div>
    </Tooltip>
  );
};

const isMultipleChoiceField = (
  fieldSchema: RJSFSchema | undefined,
  fieldUiSchema: WidgetUiSchema | undefined
): boolean => {
  const widget =
    fieldUiSchema?.["ui:widget"] ?? fieldUiSchema?.["ui:options"]?.widget;

  if (widget === "checkboxes") return true;

  return false;
};

const isChoiceField = (
  fieldSchema: RJSFSchema | undefined,
  fieldUiSchema: WidgetUiSchema | undefined
): boolean => {
  const widget =
    fieldUiSchema?.["ui:widget"] ?? fieldUiSchema?.["ui:options"]?.widget;

  if (widget === "radio" || widget === "checkboxes") return true;
  if (fieldSchema?.type === "boolean") return true;

  // we need to narrow RJSFSchema.items
  const items = (fieldSchema as any)?.items as any;
  if (fieldSchema?.type === "array" && items && Array.isArray(items.enum)) {
    return true;
  }

  return false;
};

const buildTransformErrors =
  (schema: RJSFSchema, uiSchema: UiSchema, i18n: i18n) =>
  (errors: RJSFValidationError[]) => {
    return errors.map((e) => {
      // ------------------------------------------------------------
      // ------------------------------------------------------------
      // Required fields
      // ------------------------------------------------------------
      // ------------------------------------------------------------
      if (
        e.name === "required" &&
        e.params &&
        (e.params as any).missingProperty
      ) {
        const key = (e.params as any).missingProperty as string;

        const fieldSchema: RJSFSchema | undefined = (
          schema.properties as Record<string, RJSFSchema> | undefined
        )?.[key];

        const fieldUiSchema: WidgetUiSchema | undefined = (
          uiSchema as Record<string, WidgetUiSchema> | undefined
        )?.[key];

        const title: string = fieldSchema?.title ?? key;
        const choice = isChoiceField(fieldSchema, fieldUiSchema);
        const multipleChoice = isMultipleChoiceField(
          fieldSchema,
          fieldUiSchema
        );

        e.message = choice
          ? multipleChoice
            ? i18n.t("Please select one or multiple options")
            : i18n.t("Please select one option")
          : i18n.t(`Please add {{title}}`, { title: title });

        return e;
      }

      // ------------------------------------------------------------
      // ------------------------------------------------------------
      // Array field with minItems (e.g. required checkbox groups)
      // ------------------------------------------------------------
      // ------------------------------------------------------------
      if (
        e.name === "minItems" &&
        e.params &&
        typeof (e.params as any).limit !== "undefined"
      ) {
        // RJSF reports the field under `e.property` like ".newsletter".
        const key = (e.property || "").replace(/^\./, "");

        const fieldSchema: RJSFSchema | undefined = (
          schema.properties as Record<string, RJSFSchema> | undefined
        )?.[key];

        const fieldUiSchema: WidgetUiSchema | undefined = (
          uiSchema as Record<string, WidgetUiSchema> | undefined
        )?.[key];

        const multipleChoice = isMultipleChoiceField(
          fieldSchema,
          fieldUiSchema
        );
        const choice = isChoiceField(fieldSchema, fieldUiSchema);

        // For checkbox groups we always want our friendly message instead of
        // "must NOT have fewer than X items".
        e.message = choice
          ? multipleChoice
            ? i18n.t("Please select one or multiple options")
            : i18n.t("Please select one option")
          : i18n.t("Please add {{title}}", {
              title: fieldSchema?.title ?? key,
            });

        return e;
      }

      // ------------------------------------------------------------
      // ------------------------------------------------------------
      // Number field with range (min/max)
      // ------------------------------------------------------------
      // ------------------------------------------------------------
      if (
        (e.name === "minimum" || e.name === "maximum") &&
        e.params &&
        typeof (e.params as any).limit !== "undefined"
      ) {
        const limit = (e.params as any).limit;

        if (e.name === "minimum") {
          e.message = i18n.t(
            `Please add a number that is equal to or greater than {{limit}}`,
            { limit: limit }
          );
        } else if (e.name === "maximum") {
          e.message = i18n.t(
            `Please add a number that is equal to or less than {{limit}}`,
            { limit: limit }
          );
        }

        return e;
      }

      return e;
    });
  };
